import { Button } from 'design-system';
import { LayoutDashboard } from 'lucide-react';

import type { BaseTabType } from '@/features/markets';

import { useToolApprovalPrompt, type ToolApprovalInterrupt } from './useToolApprovalPrompt';

/**
 * Renders an inline approve/deny prompt for the `add_to_dashboard` client
 * tool. Approving both resolves the tool-approval interrupt and opens the
 * real `AddToDashboardMenu` anchored to the Approve button; denying only
 * resolves the interrupt. Once the menu's add completes, the tool's result is
 * reported back to the chat via `addToolResult`.
 *
 * Rendered by `AIContent` for any `tool-call` part in
 * `state: 'approval-requested'`.
 */
const ToolApprovalPrompt = ({
  interrupt,
  tab,
  onResolved
}: {
  interrupt: ToolApprovalInterrupt;
  tab: BaseTabType;
  onResolved?: (approved: boolean) => void;
}) => {
  const { approveButtonRef, disabled, layer, handleApprove, handleDeny } = useToolApprovalPrompt(
    interrupt,
    tab,
    onResolved
  );

  return (
    <div className='my-4 flex items-center gap-3 rounded-lg border border-gx-border bg-gx-tile-dark/60 p-3 text-sm'>
      <div className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-gx-response text-gx-highlight'>
        <LayoutDashboard
          aria-hidden='true'
          className='size-4'
        />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='font-medium text-gx-text'>Add this result to your dashboard?</p>
        <p className='mt-0.5 text-xs text-gx-subtext'>You can configure it before adding.</p>
      </div>
      <div className='flex shrink-0 items-center gap-1'>
        <Button
          size='sm'
          variant='ghost'
          onClick={handleDeny}
        >
          Deny
        </Button>
        <Button
          ref={approveButtonRef}
          disabled={disabled}
          size='sm'
          onClick={handleApprove}
        >
          Approve
        </Button>
      </div>
      {layer}
    </div>
  );
};

export default ToolApprovalPrompt;
